import type React from 'react';
import {useState} from 'react';
import {Box, Button, Stack, Typography} from '@mui/material';
import {Autorenew, Edit} from '@mui/icons-material';
import {MarkdownText, RichTextField} from '../../../components/MarkdownEditor';
import type {PlanProps} from '../strategyProps';
import {PlanSection} from '../PlanSection';
import {HelpTitle} from '../SectionLinks';

const SUMMARY_HELP = 'Drafted from the figures: spend and growth over the spend period, suppliers and the largest share, '
    + 'the Kraljic quadrant (Dutch Windmill) and the savings target (PICK). Edit it as rich text, or use Redraft from '
    + 'data to rewrite it from today\u2019s figures and the plan as it stands (that replaces your text; Undo restores it).';

/**
 * Executive summary, drafted from the category's figures. Edited as rich text (bold, italic, lists, links) and stored
 * as Markdown. "Redraft from data" replaces it with a summary of today's figures and the plan as it stands; Undo in
 * the notice restores the previous text.
 */
export const SummarySection: React.FC<PlanProps> = ({plan, actions}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const text = plan.content.summary;
    const canRedraft = plan.canEdit && plan.redraftedSummary !== text;

    const startEdit = () => {
        setDraft(text);
        setEditing(true);
    };
    const save = () => {
        setEditing(false);
        const next = draft.trim();
        if (next !== text) void actions.save('summary', next, 'Summary updated.');
    };

    return (
        <PlanSection
            title={<HelpTitle title="Executive summary" help={SUMMARY_HELP} links={[]}/>}
            edited={plan.edited.includes('summary')}
            actions={!editing && plan.canEdit && (
                <>
                    {canRedraft && (
                        <Button size="small" startIcon={<Autorenew/>}
                                onClick={() => void actions.save('summary', plan.redraftedSummary, 'Summary redrafted from the data.')}>
                            Redraft from data
                        </Button>
                    )}
                    <Button size="small" startIcon={<Edit/>} onClick={startEdit}>Edit</Button>
                </>
            )}
        >
            {editing ? (
                <Stack gap={1}>
                    <RichTextField initialMarkdown={text} onChange={setDraft} ariaLabel="Executive summary" autoFocus
                                   minHeight={96} onSubmit={save} onCancel={() => setEditing(false)}/>
                    <Stack direction="row" gap={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
                        <Button size="small" variant="contained" onClick={save}>Save</Button>
                    </Stack>
                </Stack>
            ) : text.trim() ? (
                <Box onDoubleClick={() => plan.canEdit && startEdit()}><MarkdownText markdown={text}/></Box>
            ) : (
                <Typography variant="body2" color="text.secondary">No summary yet.</Typography>
            )}
        </PlanSection>
    );
};
